/**
 * Argenmap del IGN (calle / calle oscuro). Si el host no responde, no hay que
 * apagar el satélite: el mapa quedaría en negro hasta que ellos vuelvan.
 */

const IGN_PROBE_TIMEOUT_MS = 3000;
const IGN_PROBE_CACHE_MS = 5 * 60 * 1000;

const IGN_PROBE_URLS = {
  street:
    'https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/capabaseargenmap@EPSG%3A3857@png/4/5/6.png',
  streetDark:
    'https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/argenmap_oscuro@EPSG%3A3857@png/4/5/6.png',
};

/** @type {Record<string, { ok: boolean, expiresAt: number }>} */
const probeCache = {};
/** @type {Record<string, Promise<boolean>>} */
const probeInFlight = {};

/**
 * @param {string} viewId
 * @returns {boolean}
 */
export function isIgnStreetViewId(viewId) {
  return viewId === 'street' || viewId === 'streetDark';
}

/**
 * ¿El TMS de IGN entrega un tile? Usa `Image` (igual que MapLibre), no `fetch`
 * (el PNG suele no tener CORS para XHR).
 *
 * @param {'street'|'streetDark'} viewId
 * @returns {Promise<boolean>}
 */
export function probeIgnBasemap(viewId) {
  if (!isIgnStreetViewId(viewId)) return Promise.resolve(true);

  const cached = probeCache[viewId];
  if (cached && Date.now() < cached.expiresAt) {
    return Promise.resolve(cached.ok);
  }
  if (probeInFlight[viewId]) return probeInFlight[viewId];

  const url = IGN_PROBE_URLS[viewId];
  const run = loadIgnProbeImage(url)
    .then((ok) => {
      probeCache[viewId] = { ok, expiresAt: Date.now() + IGN_PROBE_CACHE_MS };
      return ok;
    })
    .finally(() => {
      delete probeInFlight[viewId];
    });

  probeInFlight[viewId] = run;
  return run;
}

/**
 * @returns {Promise<{ street: boolean, streetDark: boolean }>}
 */
export function probeIgnBasemaps() {
  return Promise.all([probeIgnBasemap('street'), probeIgnBasemap('streetDark')])
    .then(([street, streetDark]) => ({ street, streetDark }));
}

function loadIgnProbeImage(url) {
  return new Promise((resolve) => {
    if (typeof Image === 'undefined') {
      resolve(false);
      return;
    }

    const img = new Image();
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
      resolve(ok);
    };

    const timeoutId = setTimeout(() => finish(false), IGN_PROBE_TIMEOUT_MS);
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = url;
  });
}
